import { useState, useEffect, useMemo } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { SectionCard } from "@project/admin/components/module-ui";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Badge } from "@project/components/ui/badge";
import { Switch } from "@project/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { Textarea } from "@project/components/ui/textarea";
import {
  Plus,
  Trash2,
  Save,
  Globe,
  FileText,
  Users,
  Briefcase,
  Pencil,
  Search,
  ArrowLeft,
  ArrowRight,
  Info,
} from "lucide-react";
import { DeleteConfirmDialog } from "@project/admin/components/delete-confirm-dialog";
import { projectToast } from "@project/lib/toast";
import {
  hospitalProfileService,
  medicalStaffService,
} from "@project/services/admin";
import type {
  HospitalProfile,
  HospitalProfilePayload,
  Highlight,
} from "@project/types/hospital-profile";
import type { MedicalStaff } from "@project/types/medical-staff";

const DEFAULT_FORM_STATE: HospitalProfilePayload = {
  hospital_name: "",
  subtitle: "",
  monthly_surgeries: 0,
  hospital_timings: "",
  closed_on: "",
  new_patient_fee: 0,
  existing_patient_fee: 0,
  emergency_contact_name: "",
  emergency_contact_number: "",
  highlights: [],
  facilities: [],
  treatments: [],
  equipments: [],
  doctors: [],
  nurses: [],
  nibhav_yojana: [],
  is_active: true,
  order_by: 0,
  image: "",
  description: "",
  poem: "",
};

export default function HospitalProfilePage() {
  const [profiles, setProfiles] = useState<HospitalProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // CRUD Modes: 'list' | 'create' | 'edit'
  const [mode, setMode] = useState<"list" | "create" | "edit">("list");
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(
    null
  );
  const [formState, setFormState] =
    useState<HospitalProfilePayload>(DEFAULT_FORM_STATE);

  // Form Sub-tabs: 'general' | 'team' | 'services' | 'about'
  const [subTab, setSubTab] = useState<
    "general" | "team" | "services" | "about"
  >("general");

  // Medical Staff State
  const [staffList, setStaffList] = useState<MedicalStaff[]>([]);
  const [staffLoading, setStaffLoading] = useState(false);

  // Dialogs Control
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showAddNurseModal, setShowAddNurseModal] = useState(false);
  const [nurseForm, setNurseForm] = useState({
    name: "",
    role: "",
  });

  const [showItemModal, setShowItemModal] = useState(false);
  const [itemType, setItemType] = useState<
    "highlight" | "facility" | "treatment" | "equipment" | null
  >(null);
  const [highlightForm, setHighlightForm] = useState<Highlight>({
    title: "",
    description: "",
  });
  const [textItemForm, setTextItemForm] = useState("");
  const [showNibhavModal, setShowNibhavModal] = useState(false);
  const [nibhavForm, setNibhavForm] = useState({
    title: "",
    price: "",
  });

  // Fetch Hospital Profiles
  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const res = await hospitalProfileService.listPage({
        page: 1,
        page_size: 100,
      });
      setProfiles(res.data || []);
    } catch (err) {
      console.error("Failed to load hospital profiles", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Medical Staff
  const fetchStaff = async () => {
    try {
      setStaffLoading(true);
      const res = await medicalStaffService.listPage({
        page: 1,
        page_size: 100,
      });
      setStaffList(res.data || []);
    } catch (err) {
      console.error("Failed to load medical staff", err);
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    fetchStaff();
  }, []);

  // Filter Profiles
  const filteredProfiles = useMemo(() => {
    const search = q.trim().toLowerCase();
    const sorted = [...profiles].sort(
      (a, b) => (a.order_by ?? 0) - (b.order_by ?? 0)
    );
    if (!search) {
      return sorted;
    }
    return sorted.filter(
      p =>
        p.hospital_name.toLowerCase().includes(search) ||
        p.subtitle.toLowerCase().includes(search)
    );
  }, [profiles, q]);

  // Handle Edit Click
  const handleEdit = (profile: HospitalProfile) => {
    setSelectedProfileId(profile.id);
    setFormState({
      hospital_name: profile.hospital_name || "",
      subtitle: profile.subtitle || "",
      monthly_surgeries: profile.monthly_surgeries ?? 0,
      hospital_timings: profile.hospital_timings || "",
      closed_on: profile.closed_on || "",
      new_patient_fee: profile.new_patient_fee ?? 0,
      existing_patient_fee: profile.existing_patient_fee ?? 0,
      emergency_contact_name: profile.emergency_contact_name || "",
      emergency_contact_number: profile.emergency_contact_number || "",
      highlights: profile.highlights || [],
      facilities: profile.facilities || [],
      treatments: profile.treatments || [],
      equipments: profile.equipments || [],
      doctors: profile.doctors || [],
      nurses: profile.nurses || [],
      nibhav_yojana: (() => {
        const raw = (profile as any).nibhav_yojana || (profile as any).maintenance_schemes;
        if (Array.isArray(raw)) return raw;
        if (typeof raw === "string") {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
          } catch {}
        }
        return [];
      })(),
      is_active: profile.is_active === true,
      order_by: profile.order_by ?? 0,
      image: profile.image || "",
      description: profile.description || "",
      poem: profile.poem || "",
    });
    setSubTab("general");
    setMode("edit");
  };

  // Handle Create Click
  const handleCreateNew = () => {
    setSelectedProfileId(null);
    setFormState(DEFAULT_FORM_STATE);
    setSubTab("general");
    setMode("create");
  };

  // Handle Save
  const handleSaveProfile = async () => {
    if (!formState.hospital_name.trim()) {
      projectToast.error("Hospital Display Name is required.");
      return;
    }

    try {
      if (mode === "create") {
        await hospitalProfileService.create(formState);
        projectToast.success("Hospital profile created successfully");
      } else if (mode === "edit" && selectedProfileId !== null) {
        await hospitalProfileService.update(selectedProfileId, formState);
        projectToast.success("Hospital profile updated successfully");
      }
      fetchProfiles();
      setMode("list");
    } catch (err) {
      console.error("Failed to save profile", err);
    }
  };

  // Handle Delete
  const handleDeleteConfirm = async () => {
    if (deleteTargetId === null) {
      return;
    }
    try {
      setDeleteLoading(true);
      await hospitalProfileService.delete(deleteTargetId);
      projectToast.success("Hospital profile deleted successfully");
      fetchProfiles();
    } catch (err) {
      console.error("Failed to delete profile", err);
    } finally {
      setDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  // Handle Update Field
  const updateFormField = (field: keyof HospitalProfilePayload, value: any) => {
    setFormState(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle Doctor Select/Unselect
  const handleDoctorToggle = (id: number) => {
    const current = [...(formState.doctors || [])];
    const idx = current.indexOf(id);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(id);
    }
    updateFormField("doctors", current);
  };

  // Handle Add Nurse
  const handleAddNurseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nurseForm.name.trim()) {
      projectToast.error("Nurse name is required.");
      return;
    }
    const current = [...(formState.nurses || [])];
    current.push({
      name: nurseForm.name.trim(),
      role: nurseForm.role.trim() || "Nursing Staff",
    });
    updateFormField("nurses", current);
    setNurseForm({ name: "", role: "" });
    setShowAddNurseModal(false);
    projectToast.success("Nurse profile added");
  };

  const handleRemoveNurse = (idx: number) => {
    const current = [...(formState.nurses || [])];
    current.splice(idx, 1);
    updateFormField("nurses", current);
    projectToast.success("Nurse profile unlinked");
  };

  // Handle Add Item to Services Arrays
  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (itemType === "highlight") {
      if (!highlightForm.title.trim() || !highlightForm.description.trim()) {
        projectToast.error("Both title and description are required.");
        return;
      }
      const current = [...(formState.highlights || [])];
      current.push({ ...highlightForm });
      updateFormField("highlights", current);
      setHighlightForm({ title: "", description: "" });
      projectToast.success("Statistic Highlight added");
    } else if (itemType === "facility") {
      if (!textItemForm.trim()) {
        return;
      }
      const current = [...(formState.facilities || [])];
      current.push(textItemForm.trim());
      updateFormField("facilities", current);
      setTextItemForm("");
      projectToast.success("Facility amenity added");
    } else if (itemType === "treatment") {
      if (!textItemForm.trim()) {
        return;
      }
      const current = [...(formState.treatments || [])];
      current.push(textItemForm.trim());
      updateFormField("treatments", current);
      setTextItemForm("");
      projectToast.success("Treatment service added");
    } else if (itemType === "equipment") {
      if (!textItemForm.trim()) {
        return;
      }
      const current = [...(formState.equipments || [])];
      current.push(textItemForm.trim());
      updateFormField("equipments", current);
      setTextItemForm("");
      projectToast.success("Diagnostic equipment added");
    }
    setShowItemModal(false);
    setItemType(null);
  };

  // Remove items from lists
  const handleRemoveItem = (
    type: "highlights" | "facilities" | "treatments" | "equipments",
    idx: number
  ) => {
    const current = [...(formState[type] || [])];
    current.splice(idx, 1);
    updateFormField(type, current);
    projectToast.success("Item removed");
  };

  // Nibhav Yojana Handlers
  const handleAddNibhav = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nibhavForm.title.trim()) {
      projectToast.error("Scheme Title is required");
      return;
    }
    const priceNum = parseFloat(nibhavForm.price);
    if (isNaN(priceNum) || priceNum <= 0) {
      projectToast.error("Please enter a valid price");
      return;
    }
    const current = [...(formState.nibhav_yojana || [])];
    current.push({
      title: nibhavForm.title.trim(),
      price: priceNum,
    });
    updateFormField("nibhav_yojana", current);
    setNibhavForm({ title: "", price: "" });
    setShowNibhavModal(false);
    projectToast.success("Maintenance scheme added successfully");
  };

  const handleRemoveNibhav = (idx: number) => {
    const current = [...(formState.nibhav_yojana || [])];
    current.splice(idx, 1);
    updateFormField("nibhav_yojana", current);
    projectToast.success("Maintenance scheme removed successfully");
  };

  // Doctors List (Filtered)
  const doctorsList = useMemo(() => {
    return staffList.filter(s => s.staff_type === "doctor");
  }, [staffList]);

  return (
    <AdminShell
      title="Healthcare Details"
      subtitle="Configure timings, consultation fees, highlight statistics, equipment, doctors, and nurses rosters for public care sections."
    >
      {mode === "list" ? (
        <div className="space-y-4">
          <SectionCard
            title="Hospital Profiles Directory"
            subtitle="Manage displayed info cards for healthcare units"
            actions={
              <Button
                size="sm"
                onClick={handleCreateNew}
                className="gap-1.5 text-xs font-bold"
              >
                <Plus className="h-3.5 w-3.5" /> Add Hospital Profile
              </Button>
            }
          >
            <div className="mb-4 flex items-center gap-3">
              <div className="relative max-w-sm flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by hospital name or subtitle..."
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  className="h-9 pl-9 text-xs"
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="text-xs font-bold">
                      Hospital/Unit Name
                    </TableHead>
                    <TableHead className="text-xs font-bold">
                      Subtitle
                    </TableHead>
                    <TableHead className="text-xs font-bold">Timings</TableHead>
                    <TableHead className="text-xs font-bold">
                      Emergency Contact
                    </TableHead>
                    <TableHead className="text-center text-xs font-bold">
                      Order
                    </TableHead>
                    <TableHead className="text-xs font-bold">Status</TableHead>
                    <TableHead className="text-right text-xs font-bold">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-6 text-center text-xs text-muted-foreground"
                      >
                        Loading hospital profiles...
                      </TableCell>
                    </TableRow>
                  ) : filteredProfiles.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-6 text-center text-xs text-muted-foreground"
                      >
                        No hospital profiles registered.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredProfiles.map(p => (
                      <TableRow key={p.id} className="text-xs">
                        <TableCell className="max-w-[200px] truncate font-bold text-foreground">
                          {p.hospital_name}
                        </TableCell>
                        <TableCell className="max-w-[250px] truncate text-muted-foreground">
                          {p.subtitle || "—"}
                        </TableCell>
                        <TableCell>{p.hospital_timings || "—"}</TableCell>
                        <TableCell className="font-medium text-slate-700">
                          {p.emergency_contact_name ? (
                            <div>
                              <p className="font-semibold text-foreground">
                                {p.emergency_contact_name}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {p.emergency_contact_number || "—"}
                              </p>
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-center font-semibold text-muted-foreground">
                          {p.order_by ?? 0}
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={
                              p.is_active
                                ? "bg-emerald-500/15 font-bold text-emerald-700 shadow-none hover:bg-emerald-500/15"
                                : "bg-slate-500/15 font-bold text-slate-700 shadow-none hover:bg-slate-500/15"
                            }
                          >
                            {p.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleEdit(p)}
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setDeleteTargetId(p.id)}
                              className="hover:bg-destructive/10 h-8 w-8 text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        </div>
      ) : (
        <div className="animate-fade-in space-y-6">
          {/* Header Action Bar */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMode("list")}
              className="shadow-xs h-9 gap-1 rounded-xl text-xs font-bold"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Directory
            </Button>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                <Globe className="h-4 w-4 animate-pulse text-emerald-600" />{" "}
                Applies to healthcare details public section
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row">
            {/* Left Nav Tabs */}
            <div className="w-full shrink-0 space-y-1 lg:w-64">
              <Button
                variant={subTab === "general" ? "default" : "outline"}
                onClick={() => setSubTab("general")}
                className="h-11 w-full justify-start gap-2 rounded-xl p-3 text-xs font-bold shadow-none"
              >
                <FileText className="h-4 w-4" /> 1. Timings & Fees Info
              </Button>
              <Button
                variant={subTab === "team" ? "default" : "outline"}
                onClick={() => {
                  if (!formState.hospital_name.trim()) {
                    projectToast.error("Hospital Display Name is required.");
                    return;
                  }
                  setSubTab("team");
                }}
                className="h-11 w-full justify-start gap-2 rounded-xl p-3 text-xs font-bold shadow-none"
              >
                <Users className="h-4 w-4" /> 2. Doctors & Nurses Team
              </Button>
              <Button
                variant={subTab === "services" ? "default" : "outline"}
                onClick={() => {
                  if (!formState.hospital_name.trim()) {
                    projectToast.error("Hospital Display Name is required.");
                    return;
                  }
                  setSubTab("services");
                }}
                className="h-11 w-full justify-start gap-2 rounded-xl p-3 text-xs font-bold shadow-none"
              >
                <Briefcase className="h-4 w-4" /> 3. Services & Equipment
              </Button>
              <Button
                variant={subTab === "about" ? "default" : "outline"}
                onClick={() => {
                  if (!formState.hospital_name.trim()) {
                    projectToast.error("Hospital Display Name is required.");
                    return;
                  }
                  setSubTab("about");
                }}
                className="h-11 w-full justify-start gap-2 rounded-xl p-3 text-xs font-bold shadow-none"
              >
                <Info className="h-4 w-4" /> 4. About Info
              </Button>
            </div>

            {/* Editor Canvas */}
            <div className="flex-1 space-y-6">
              {/* TAB 1: GENERAL INFO */}
              {subTab === "general" && (
                <SectionCard
                  title="Timings & Consultation Info"
                  subtitle="Configure Display Name, subtitle timings, fees and emergency contacts."
                >
                  <div className="grid gap-4 py-3 text-xs font-semibold">
                    <div className="grid gap-2">
                      <Label htmlFor="hospital_name">
                        Hospital Display Name *
                      </Label>
                      <Input
                        id="hospital_name"
                        required
                        value={formState.hospital_name}
                        onChange={e =>
                          updateFormField("hospital_name", e.target.value)
                        }
                        className="h-10 text-xs font-medium"
                        placeholder="e.g. Bhagwan Mahavir Eye Care Hospital"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="subtitle">Short Subtitle / Tagline</Label>
                      <Input
                        id="subtitle"
                        value={formState.subtitle}
                        onChange={e =>
                          updateFormField("subtitle", e.target.value)
                        }
                        className="h-10 text-xs font-medium"
                        placeholder="e.g. Advanced Ophthalmologic Services & Phaco Cataract Surgeries"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="grid gap-2">
                        <Label htmlFor="hospital_timings">
                          Hospital / Clinic Timings
                        </Label>
                        <Input
                          id="hospital_timings"
                          value={formState.hospital_timings}
                          onChange={e =>
                            updateFormField("hospital_timings", e.target.value)
                          }
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. Monday to Saturday |9:00 AM to 1:00 PM"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="closed_on">Closed On / Off-Days</Label>
                        <Input
                          id="closed_on"
                          value={formState.closed_on}
                          onChange={e =>
                            updateFormField("closed_on", e.target.value)
                          }
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. Sundays & Festivals"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                      <div className="grid gap-2">
                        <Label htmlFor="new_patient_fee">
                          New Patient Fee (₹)
                        </Label>
                        <Input
                          id="new_patient_fee"
                          type="number"
                          value={formState.new_patient_fee ?? ""}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            updateFormField(
                              "new_patient_fee",
                              isNaN(val) ? 0 : val
                            );
                          }}
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. 50"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="existing_patient_fee">
                          Existing Patient Fee (₹)
                        </Label>
                        <Input
                          id="existing_patient_fee"
                          type="number"
                          value={formState.existing_patient_fee ?? ""}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            updateFormField(
                              "existing_patient_fee",
                              isNaN(val) ? 0 : val
                            );
                          }}
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. 30"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="monthly_surgeries">
                          Monthly Surgeries Count
                        </Label>
                        <Input
                          id="monthly_surgeries"
                          type="number"
                          value={formState.monthly_surgeries ?? ""}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            updateFormField(
                              "monthly_surgeries",
                              isNaN(val) ? 0 : val
                            );
                          }}
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. 125"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="order_by">
                          Order By
                        </Label>
                        <Input
                          id="order_by"
                          type="number"
                          value={formState.order_by ?? ""}
                          onChange={e => {
                            const val = parseInt(e.target.value, 10);
                            updateFormField(
                              "order_by",
                              isNaN(val) ? 0 : val
                            );
                          }}
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. 0"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="grid gap-2">
                        <Label htmlFor="emergency_contact_name">
                          Emergency Contact Name
                        </Label>
                        <Input
                          id="emergency_contact_name"
                          value={formState.emergency_contact_name}
                          onChange={e =>
                            updateFormField(
                              "emergency_contact_name",
                              e.target.value
                            )
                          }
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. Sejal Shah (Medical In-charge)"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="emergency_contact_number">
                          Emergency Contact Number
                        </Label>
                        <Input
                          id="emergency_contact_number"
                          value={formState.emergency_contact_number}
                          onChange={e =>
                            updateFormField(
                              "emergency_contact_number",
                              e.target.value
                            )
                          }
                          className="h-10 text-xs font-medium"
                          placeholder="e.g. 9978930950"
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-border pt-4">
                      <div>
                        <Label
                          htmlFor="is_active"
                          className="text-sm font-bold"
                        >
                          Active Status
                        </Label>
                        <p className="mt-0.5 text-[11px] font-normal text-muted-foreground">
                          Inactive profiles will be hidden from the public
                          website.
                        </p>
                      </div>
                      <Switch
                        id="is_active"
                        checked={formState.is_active}
                        onCheckedChange={val =>
                          updateFormField("is_active", val)
                        }
                      />
                    </div>
                  </div>
                </SectionCard>
              )}

              {/* TAB 2: DOCTORS & NURSES TEAM */}
              {subTab === "team" && (
                <div className="space-y-6">
                  {/* Doctors Section */}
                  <SectionCard
                    title="Doctors & Specialists Selection"
                    subtitle="Associate practitioners from the Medical Staff directory with this hospital unit."
                    actions={
                      <div className="w-56">
                        <Select
                          onValueChange={val => {
                            const id = Number(val);
                            if (id) {
                              const current = [...(formState.doctors || [])];
                              if (!current.includes(id)) {
                                current.push(id);
                                updateFormField("doctors", current);
                                projectToast.success(
                                  "Doctor linked successfully"
                                );
                              }
                            }
                          }}
                          value=""
                        >
                          <SelectTrigger className="h-8 rounded-lg text-[11px] font-bold">
                            <SelectValue placeholder="Add Doctor..." />
                          </SelectTrigger>
                          <SelectContent className="border-border bg-background">
                            {doctorsList
                              .filter(
                                d => !(formState.doctors || []).includes(d.id)
                              )
                              .map(d => (
                                <SelectItem
                                  key={d.id}
                                  value={String(d.id)}
                                  className="text-xs"
                                >
                                  {d.name} ({d.qualification || "MD"})
                                </SelectItem>
                              ))}
                            {doctorsList.filter(
                              d => !(formState.doctors || []).includes(d.id)
                            ).length === 0 && (
                                <SelectItem
                                  value="none"
                                  disabled
                                  className="text-xs"
                                >
                                  No doctors available to add
                                </SelectItem>
                              )}
                          </SelectContent>
                        </Select>
                      </div>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {staffLoading ? (
                        <p className="col-span-2 py-4 text-center text-xs text-muted-foreground">
                          Loading staff...
                        </p>
                      ) : (formState.doctors || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No doctors linked to this profile. Choose a
                          practitioner from the dropdown.
                        </p>
                      ) : (
                        (formState.doctors || []).map(docId => {
                          const doc = doctorsList.find(d => d.id === docId);
                          if (!doc) {
                            return null;
                          }
                          return (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 p-2.5 text-xs transition hover:bg-slate-50"
                            >
                              <div className="min-w-0">
                                <p className="truncate font-bold text-foreground">
                                  {doc.name}
                                </p>
                                <p className="truncate text-[10px] text-muted-foreground">
                                  {doc.qualification} · {doc.designation}
                                </p>
                              </div>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleDoctorToggle(doc.id)}
                                className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </SectionCard>

                  {/* Nurses Section */}
                  <SectionCard
                    title="Nursing & Clinical Assistants"
                    subtitle="Manually configure the nurse profiles displayed on the public site."
                    actions={
                      <Button
                        size="sm"
                        onClick={() => {
                          setNurseForm({ name: "", role: "" });
                          setShowAddNurseModal(true);
                        }}
                        className="h-8 gap-1 rounded-lg bg-primary text-[11px] font-bold text-white shadow-sm"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Nurse
                      </Button>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(formState.nurses || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No nurses added. Click Add Nurse to configure.
                        </p>
                      ) : (
                        (formState.nurses || []).map(n => (
                          <div
                            key={n.name}
                            className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 p-2.5 text-xs transition hover:bg-slate-50"
                          >
                            <div className="min-w-0">
                              <p className="truncate font-bold text-foreground">
                                {n.name}
                              </p>
                              <p className="truncate text-[10px] text-muted-foreground">
                                {n.role}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRemoveNurse(idx)}
                              className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* TAB 3: SERVICES & EQUIPMENT */}
              {subTab === "services" && (
                <div className="animate-fade-in space-y-6">
                  {/* Highlights Stats */}
                  <SectionCard
                    title="Website Statistics & Highlights"
                    subtitle="Highlights with badges shown as callouts (e.g. 125+ Monthly Surgeries)"
                    actions={
                      <Button
                        size="sm"
                        onClick={() => {
                          setItemType("highlight");
                          setHighlightForm({ title: "", description: "" });
                          setShowItemModal(true);
                        }}
                        className="h-8 gap-1 rounded-lg bg-primary text-[11px] font-bold text-white shadow-sm"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Highlight
                      </Button>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(formState.highlights || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No key statistics logged.
                        </p>
                      ) : (
                        (formState.highlights || []).map((hl, idx) => (
                          <div
                            key={hl.title}
                            className="flex items-start justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/50 p-3 text-xs transition hover:bg-slate-50"
                          >
                            <div className="min-w-0">
                              <p className="truncate font-bold text-primary">
                                {hl.title}
                              </p>
                              <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                                {hl.description}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                handleRemoveItem("highlights", idx)
                              }
                              className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </SectionCard>

                  {/* Treatments */}
                  <SectionCard
                    title="Treatments & Clinic Services"
                    subtitle="Procedures, consults, and health checks offered by this clinic"
                    actions={
                      <Button
                        size="sm"
                        onClick={() => {
                          setItemType("treatment");
                          setTextItemForm("");
                          setShowItemModal(true);
                        }}
                        className="h-8 gap-1 rounded-lg bg-primary text-[11px] font-bold text-white shadow-sm"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Service
                      </Button>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(formState.treatments || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No services added.
                        </p>
                      ) : (
                        (formState.treatments || []).map((tx, idx) => (
                          <div
                            key={tx}
                            className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 p-2.5 text-xs transition hover:bg-slate-50"
                          >
                            <span className="max-w-[85%] truncate font-medium text-foreground">
                              {tx}
                            </span>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                handleRemoveItem("treatments", idx)
                              }
                              className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </SectionCard>

                  {/* Maintenance Schemes Section */}
                  <SectionCard
                    title="Maintenance Schemes"
                    subtitle="Maintenance & Care Schemes with donation pricing (Title & Price)"
                    actions={
                      <Button
                        size="sm"
                        onClick={() => {
                          setNibhavForm({ title: "", price: "" });
                          setShowNibhavModal(true);
                        }}
                        className="h-8 gap-1 rounded-lg bg-primary text-[11px] font-bold text-white shadow-sm"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Maintenance Scheme
                      </Button>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(formState.nibhav_yojana || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No maintenance schemes added yet. Click "+ Add Maintenance Scheme" to add.
                        </p>
                      ) : (
                        (formState.nibhav_yojana || []).map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 p-3 text-xs transition hover:bg-slate-50"
                          >
                            <div className="min-w-0 flex-1 pr-2">
                              <p className="truncate font-bold text-foreground">
                                {item.title}
                              </p>
                              <p className="mt-0.5 text-[11px] font-semibold text-emerald-700">
                                ₹{Number(item.price).toLocaleString("en-IN")}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRemoveNibhav(idx)}
                              className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </SectionCard>

                  {/* Equipment */}
                  <SectionCard
                    title="Diagnostic Equipment & Tools"
                    subtitle="Important diagnostic devices or machines deployed at this unit"
                    actions={
                      <Button
                        size="sm"
                        onClick={() => {
                          setItemType("equipment");
                          setTextItemForm("");
                          setShowItemModal(true);
                        }}
                        className="h-8 gap-1 rounded-lg bg-primary text-[11px] font-bold text-white shadow-sm"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Equipment
                      </Button>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(formState.equipments || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No diagnostic machines registered.
                        </p>
                      ) : (
                        (formState.equipments || []).map((eq, idx) => (
                          <div
                            key={eq}
                            className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 p-2.5 text-xs transition hover:bg-slate-50"
                          >
                            <span className="max-w-[85%] truncate font-medium text-foreground">
                              {eq}
                            </span>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                handleRemoveItem("equipments", idx)
                              }
                              className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </SectionCard>

                  {/* Facilities */}
                  <SectionCard
                    title="Facility Amenities"
                    subtitle="General patient comforts (e.g. Oxygen Support, First Aid, Ramp access)"
                    actions={
                      <Button
                        size="sm"
                        onClick={() => {
                          setItemType("facility");
                          setTextItemForm("");
                          setShowItemModal(true);
                        }}
                        className="h-8 gap-1 rounded-lg bg-primary text-[11px] font-bold text-white shadow-sm"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add Facility
                      </Button>
                    }
                  >
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {(formState.facilities || []).length === 0 ? (
                        <p className="col-span-2 rounded-xl border border-dashed border-border py-4 text-center text-xs text-muted-foreground">
                          No facility features added.
                        </p>
                      ) : (
                        (formState.facilities || []).map((fc, idx) => (
                          <div
                            key={fc}
                            className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 p-2.5 text-xs transition hover:bg-slate-50"
                          >
                            <span className="max-w-[85%] truncate font-medium text-foreground">
                              {fc}
                            </span>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                handleRemoveItem("facilities", idx)
                              }
                              className="hover:bg-destructive/10 h-7 w-7 shrink-0 rounded-lg text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* TAB 4: ABOUT DETAILS */}
              {subTab === "about" && (
                <SectionCard
                  title="About Details"
                  subtitle="Configure About image url and description for the public page."
                >
                  <div className="grid gap-4 py-3 text-xs font-semibold">
                    <div className="grid gap-2">
                      <Label>Image</Label>
                      <div className="flex flex-col gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          id="image-file-input"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                              updateFormField("image", file);
                            }
                          }}
                        />
                        {formState.image ? (
                          <div className="relative w-48 h-48 border border-dashed border-border rounded-xl overflow-hidden group">
                            <img
                              src={
                                formState.image instanceof File
                                  ? URL.createObjectURL(formState.image)
                                  : typeof formState.image === "string"
                                    ? formState.image
                                    : ""
                              }
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition">
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                onClick={() => {
                                  updateFormField("image", "");
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="icon"
                                onClick={() => {
                                  document.getElementById("image-file-input")?.click();
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => {
                              document.getElementById("image-file-input")?.click();
                            }}
                            className="flex flex-col items-center justify-center border-2 border-dashed border-border hover:border-primary/50 transition cursor-pointer rounded-xl w-48 h-48 text-muted-foreground gap-2"
                          >
                            <Plus className="h-8 w-8 text-muted-foreground" />
                            <span className="text-[11px] font-bold">Upload Image File</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        rows={5}
                        value={formState.description || ""}
                        onChange={e =>
                          updateFormField("description", e.target.value)
                        }
                        placeholder="Configure hospital or clinic description..."
                      />
                    </div>
                    <div className="grid gap-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="poem">Poem / Poetic Verse / Motto</Label>
                        <span className="text-[11px] font-normal text-muted-foreground">
                          Optional
                        </span>
                      </div>
                      <Textarea
                        id="poem"
                        rows={4}
                        value={formState.poem || ""}
                        onChange={e =>
                          updateFormField("poem", e.target.value)
                        }
                        placeholder="Configure poem, verse, or inspirational quote..."
                        className="min-h-[96px] resize-y text-xs font-medium leading-relaxed"
                      />
                    </div>
                  </div>
                </SectionCard>
              )}

              {/* Bottom Canvas Save Action Bar */}
              <div className="p-4.5 shadow-xs flex items-center justify-between rounded-2xl">
                {subTab === "general" ? (
                  <Button
                    variant="outline"
                    onClick={() => setMode("list")}
                    className="h-10 rounded-xl px-5 text-xs font-bold"
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (subTab === "about") setSubTab("services");
                      else if (subTab === "services") setSubTab("team");
                      else if (subTab === "team") setSubTab("general");
                    }}
                    className="h-10 gap-1.5 rounded-xl px-5 text-xs font-bold"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </Button>
                )}

                {subTab === "about" ? (
                  <Button
                    onClick={handleSaveProfile}
                    className="h-10 gap-2 rounded-xl bg-emerald-600 px-6 text-xs font-bold text-white shadow-sm hover:bg-emerald-700"
                  >
                    <Save className="h-4 w-4" /> Save Profile Details
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (
                        subTab === "general" &&
                        !formState.hospital_name.trim()
                      ) {
                        projectToast.error(
                          "Hospital Display Name is required."
                        );
                        return;
                      }
                      if (subTab === "general") setSubTab("team");
                      else if (subTab === "team") setSubTab("services");
                      else if (subTab === "services") setSubTab("about");
                    }}
                    className="hover:bg-primary/95 h-10 gap-1.5 rounded-xl bg-primary px-6 text-xs font-bold text-white"
                  >
                    Next Step <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------- DIALOG FORM MODALS ------------------- */}

      {/* Add Manual Nurse Dialog */}
      <Dialog open={showAddNurseModal} onOpenChange={setShowAddNurseModal}>
        <DialogContent className="max-w-md rounded-2xl border-border bg-background shadow-xl">
          <form onSubmit={handleAddNurseSubmit}>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-foreground">
                Add Nurse / Clinical Assistant
              </DialogTitle>
              <DialogDescription className="text-xs">
                Manually record a nurse profile for this hospital unit.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4 text-xs font-semibold">
              <div className="grid gap-2">
                <Label htmlFor="nurse_name">Full Name *</Label>
                <Input
                  id="nurse_name"
                  required
                  value={nurseForm.name}
                  onChange={e =>
                    setNurseForm(prev => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g. Nimishaben Chauhan"
                  className="h-10 text-xs font-medium"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="nurse_role">Role / Designation *</Label>
                <Input
                  id="nurse_role"
                  required
                  value={nurseForm.role}
                  onChange={e =>
                    setNurseForm(prev => ({ ...prev, role: e.target.value }))
                  }
                  placeholder="e.g. Nursing Staff / OT Assistant"
                  className="h-10 text-xs font-medium"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddNurseModal(false)}
                className="h-10 rounded-xl"
              >
                Cancel
              </Button>
              <Button type="submit" className="h-10 rounded-xl">
                Add Nurse
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Grid Multi-Use Adding Dialog */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-md rounded-2xl border-border bg-background shadow-xl">
          <form onSubmit={handleAddItem}>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-foreground">
                Add{" "}
                {itemType === "highlight"
                  ? "Statistic Highlight"
                  : itemType === "facility"
                    ? "Facility Amenity"
                    : itemType === "treatment"
                      ? "Treatment Service"
                      : "Diagnostic Equipment"}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Log a new entry into the lists of services, equipment,
                highlights, or facility features.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4 text-xs font-semibold">
              {itemType === "highlight" ? (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="hl_title">Statistic Value / Title *</Label>
                    <Input
                      id="hl_title"
                      required
                      value={highlightForm.title}
                      onChange={e =>
                        setHighlightForm(prev => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="e.g. 125+ Monthly Surgeries"
                      className="h-10 text-xs font-medium"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="hl_desc">Stat Description *</Label>
                    <Input
                      id="hl_desc"
                      required
                      value={highlightForm.description}
                      onChange={e =>
                        setHighlightForm(prev => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="e.g. Cataract Surgeries Every Month"
                      className="h-10 text-xs font-medium"
                    />
                  </div>
                </>
              ) : (
                <div className="grid gap-2">
                  <Label htmlFor="item_text">Item Text Description *</Label>
                  <Input
                    id="item_text"
                    required
                    value={textItemForm}
                    onChange={e => setTextItemForm(e.target.value)}
                    placeholder={
                      itemType === "facility"
                        ? "e.g. 24x7 Ambulance Support"
                        : itemType === "treatment"
                          ? "e.g. Glaucoma Diagnosis & Treatment"
                          : "e.g. Carl ZEISS Microscope (Germany)"
                    }
                    className="h-10 text-xs font-medium"
                  />
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowItemModal(false);
                  setItemType(null);
                }}
                className="h-10 rounded-xl"
              >
                Cancel
              </Button>
              <Button type="submit" className="h-10 rounded-xl">
                Add Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Maintenance Scheme Modal */}
      <Dialog open={showNibhavModal} onOpenChange={setShowNibhavModal}>
        <DialogContent className="border-border bg-background sm:max-w-md">
          <form onSubmit={handleAddNibhav}>
            <DialogHeader>
              <DialogTitle className="font-serif text-base font-bold">
                Add Maintenance Scheme
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Enter title and donation price for this maintenance / care scheme.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4 text-xs font-semibold">
              <div className="grid gap-2">
                <Label htmlFor="nibhav_title">Scheme Title *</Label>
                <Input
                  id="nibhav_title"
                  required
                  value={nibhavForm.title}
                  onChange={e =>
                    setNibhavForm(prev => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="e.g. Dental Clinic Maintenance / 1 Year Medicine Support"
                  className="h-10 text-xs font-medium"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="nibhav_price">Price (₹) *</Label>
                <Input
                  id="nibhav_price"
                  type="number"
                  min="1"
                  step="any"
                  required
                  value={nibhavForm.price}
                  onChange={e =>
                    setNibhavForm(prev => ({ ...prev, price: e.target.value }))
                  }
                  placeholder="e.g. 2500"
                  className="h-10 text-xs font-medium"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNibhavModal(false)}
                className="h-10 rounded-xl"
              >
                Cancel
              </Button>
              <Button type="submit" className="h-10 rounded-xl">
                Add Maintenance Scheme
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={val => {
          if (!val) {
            setDeleteTargetId(null);
          }
        }}
        loading={deleteLoading}
        onConfirm={handleDeleteConfirm}
        title="Delete Hospital Profile"
        description="Are you sure you want to delete this hospital/clinic profile? This action will permanently remove it from the database."
      />
    </AdminShell>
  );
}
